import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { ReportProblem as ReportProblemIcon } from '@mui/icons-material';

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'inappropriate', label: 'Inappropriate Content' },
  { value: 'fake', label: 'Fake Review' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'other', label: 'Other' },
];

export default function ReportModal({ open, onClose, onSubmit, loading = false }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [errors, setErrors] = useState({});

  const handleReasonChange = (event) => {
    setReason(event.target.value);
    setErrors({ ...errors, reason: '' });
  };

  const handleDetailsChange = (event) => {
    setDetails(event.target.value);
    setErrors({ ...errors, details: '' });
  };

  const handleSubmit = () => {
    // Validation
    const newErrors = {};
    
    if (!reason) {
      newErrors.reason = 'Please select a reason';
    }
    
    if (reason === 'other' && !details.trim()) {
      newErrors.details = 'Please provide details when selecting "Other"';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Submit
    onSubmit(reason, details);
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setDetails('');
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReportProblemIcon color="error" />
          <Typography variant="h6" component="span">
            Report Review
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Help us keep OnlyStats safe and trustworthy. Please select a reason for reporting this review.
        </Typography>
        
        <FormControl component="fieldset" error={!!errors.reason} fullWidth sx={{ mb: 3 }}>
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
            Reason for reporting
          </FormLabel>
          <RadioGroup
            value={reason}
            onChange={handleReasonChange}
          >
            {REPORT_REASONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {errors.reason && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {errors.reason}
            </Typography>
          )}
        </FormControl>
        
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Additional details (optional)"
          placeholder="Provide any additional information that might help us review this report..."
          value={details}
          onChange={handleDetailsChange}
          error={!!errors.details}
          helperText={errors.details || (reason === 'other' ? 'Please provide details' : 'Optional, but helpful for our review')}
          disabled={loading}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={loading || !reason}
          startIcon={loading ? <CircularProgress size={16} /> : <ReportProblemIcon />}
        >
          {loading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

